import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import { Link2, Tag, Send, RotateCcw } from 'lucide-react';

export default function ArenaIngestor() {
    const { t } = useTranslation();
    const [note, setNote] = useState("");
    const [author, setAuthor] = useState(""); // Default, could be dynamic
    const [preview, setPreview] = useState({ tags: [], links: {} });
    const [logs, setLogs] = useState([]);
    const [status, setStatus] = useState("idle"); // idle, submitting, success, error

    // Fetch existing logs on mount
    useEffect(() => {
        fetchLogs();
    }, []);

    const fetchLogs = async () => {
        try {
            // Mock logs for demo
            const mockLogs = [
                {
                    id: 1,
                    author: "Tech Team",
                    content: "The main stage motor (asset:MOT-01) is acting up on project:P2505. Need to check the rigging points.",
                    timestamp: new Date().toISOString(),
                    links: { rentman_asset_id: "MOT-01", rentman_project_id: "P2505" },
                    tags: []
                },
                {
                    id: 2,
                    author: "Tech Team",
                    content: "Lighting desk programming complete for show:TheVoice2026. All cues tested and verified.",
                    timestamp: new Date(Date.now() - 3600000).toISOString(),
                    links: { gma3_showfile_name: "TheVoice2026" },
                    tags: ["status:complete"]
                }
            ];
            setLogs(mockLogs);
        } catch (e) {
            console.error("Failed to fetch logs", e);
        }
    };

    // Live parsing logic (ported from index.html)
    useEffect(() => {
        const links = {};
        const tags = [];
        // Regex to find key:value pairs
        // We look for patterns like "project:123", "asset:ABC", "tag:urgent"
        const regex = /([a-zA-Z_]+):(\S+)/g;
        let match;

        // We only scan, we don't modify the content for the preview (unless we wanted to highlight)
        while ((match = regex.exec(note)) !== null) {
            const key = match[1].toLowerCase();
            const value = match[2];

            if (key === 'project' || key === 'proj') {
                links.rentman_project_id = value;
            } else if (key === 'asset' || key === 'item') {
                links.rentman_asset_id = value;
            } else if (key === 'show') {
                links.gma3_showfile_name = value;
            } else {
                tags.push(`${key}:${value}`);
            }
        }
        setPreview({ tags, links });
    }, [note]);

    const handleSubmit = async () => {
        setStatus("submitting");
        try {
            const payload = {
                author,
                content: note,
                category: "General",
                tags: preview.tags,
                links: preview.links
            };

            const response = await fetch('http://localhost:3001/api/arenalogs/', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload),
            });

            if (!response.ok) throw new Error('Failed to submit');

            const newLog = await response.json();
            setLogs([newLog, ...logs]);
            setNote(""); // Clear input
            setStatus("success");
            setTimeout(() => setStatus("idle"), 3000);
        } catch (e) {
            console.error(e);
            setStatus("error");
        }
    };

    return (
        <div className="space-y-6 pb-20 p-2">
            <Card className="border-blue-100 shadow-md">
                <CardHeader className="bg-gradient-to-r from-slate-50 to-blue-50/50">
                    <CardTitle className="flex items-center gap-2">
                        <Link2 className="text-blue-600" />
                        {t('arena_integration')}
                    </CardTitle>
                    <CardDescription>
                        {t('arena_description')}
                    </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4 pt-4">
                    <div className="space-y-2">
                        <Label>{t('author')}</Label>
                        <Input value={author} onChange={e => setAuthor(e.target.value)} />
                    </div>

                    <div className="space-y-2">
                        <Label>Note / Log</Label>
                        <Textarea
                            value={note}
                            onChange={e => setNote(e.target.value)}
                            placeholder="E.g., The main stage motor (asset:MOT-01) is acting up on project:P2505."
                            className="min-h-[150px] font-mono text-sm"
                        />
                    </div>

                    {/* Live Preview of Parsed Data */}
                    {(preview.tags.length > 0 || Object.keys(preview.links).length > 0) && (
                        <div className="bg-slate-50 p-3 rounded-md border border-slate-200 text-sm">
                            <p className="font-semibold text-xs text-slate-500 uppercase mb-2">{t('detected_metadata')}</p>
                            <div className="flex flex-wrap gap-2">
                                {Object.entries(preview.links).map(([k, v]) => (
                                    <Badge key={k} variant="secondary" className="bg-blue-100 text-blue-800 border-blue-200">
                                        {k.replace('_id', '')}: {v}
                                    </Badge>
                                ))}
                                {preview.tags.map(tag => (
                                    <Badge key={tag} variant="outline" className="border-slate-300">
                                        <Tag className="w-3 h-3 mr-1 opacity-50" /> {tag}
                                    </Badge>
                                ))}
                            </div>
                        </div>
                    )}

                    <div className="pt-2">
                        <Button onClick={handleSubmit} disabled={!note || status === 'submitting'} className="w-full">
                            {status === 'submitting' ? <RotateCcw className="animate-spin mr-2" /> : <Send className="mr-2 w-4 h-4" />}
                            {status === 'success' ? t('success') : t('ingest_note')}
                        </Button>
                        {status === 'error' && <p className="text-red-500 text-sm text-center mt-2">{t('error')}</p>}
                    </div>
                </CardContent>
            </Card>

            <div className="space-y-4">
                <h3 className="font-bold text-lg text-gray-700 px-1">{t('recent_logs')}</h3>
                {logs.map(log => (
                    <Card key={log.id} className="overflow-hidden">
                        <div className="bg-gray-50 px-4 py-2 border-b flex justify-between items-center text-xs text-gray-500">
                            <span>{new Date(log.timestamp).toLocaleString()}</span>
                            <span>{log.author}</span>
                        </div>
                        <CardContent className="pt-4 pb-2">
                            <p className="whitespace-pre-wrap text-sm text-gray-800">{log.content}</p>
                        </CardContent>
                        {(log.links && Object.keys(log.links).length > 0) && (
                            <div className="px-4 pb-3 flex gap-2">
                                {Object.entries(log.links).map(([k, v]) => (
                                    <Badge key={k} variant="secondary" className="text-[10px] h-5">
                                        {k.split('_')[1] || k}: {v}
                                    </Badge>
                                ))}
                            </div>
                        )}
                    </Card>
                ))}
            </div>
        </div>
    );
}